// src/AdminDashboard.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

import {
  Formik,
  Form,
  Field,
  ErrorMessage,
  FieldArray,
  useFormik,
} from "formik";
import * as Yup from "yup";
import {
  Plus,
  Mail,
  Lock,
  Trash2,
  Percent,
  X,
  Key,
  Copy,
  Eye,
  EyeOff,
  Edit3,
} from "lucide-react";
import {
  listAllLandlords,
  createLandlord,
  updateLandlord,
  deleteLandlord,
  changePassword,
} from "../api/Landlords";
import Loader from "../components/Loader";
const prettyDate = (iso) =>
  new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });

const clamp = (n, min, max) => Math.min(Math.max(n, min), max);
function Pagination({
  total = 0,
  page = 1,
  pageSize = 15,
  onChange,
  maxButtons = 5, // how many numbered buttons to show
}) {
  if (total === 0) return null;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const clamped = Math.min(Math.max(1, page), totalPages);
  const start = (clamped - 1) * pageSize + 1;
  const end = Math.min(start + pageSize - 1, total);

  // build page button window with ellipsis
  const pages = [];
  const half = Math.floor(maxButtons / 2);
  let from = Math.max(1, clamped - half);
  let to = Math.min(totalPages, from + maxButtons - 1);
  from = Math.max(1, to - maxButtons + 1);

  if (from > 1) {
    pages.push(1);
    if (from > 2) pages.push("ellipsis-left");
  }
  for (let p = from; p <= to; p++) pages.push(p);
  if (to < totalPages) {
    if (to < totalPages - 1) pages.push("ellipsis-right");
    pages.push(totalPages);
  }

  const btnBase =
    "inline-flex items-center justify-center h-9 min-w-9 px-3 rounded-lg border text-sm transition";
  const btnGhost = "border-transparent hover:bg-gray-100/70 text-gray-700";
  const btnActive = "border-blue-600 bg-blue-600 text-white hover:bg-blue-700";
  const iconBtn =
    "inline-flex items-center justify-center h-9 w-9 rounded-lg border border-gray-200 hover:bg-gray-100/70 disabled:opacity-50";

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-3 py-3">
      <div className="text-xs text-gray-600">
        Showing <span className="font-medium">{start}</span>–
        <span className="font-medium">{end}</span> of{" "}
        <span className="font-medium">{total}</span>
      </div>

      <div className="flex items-center gap-1">
        <button
          type="button"
          className={iconBtn}
          onClick={() => onChange(1)}
          disabled={clamped === 1}
          aria-label="First page"
        >
          <ChevronsLeft className="h-4 w-4" />
        </button>
        <button
          type="button"
          className={iconBtn}
          onClick={() => onChange(clamped - 1)}
          disabled={clamped === 1}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        {pages.map((p, idx) =>
          typeof p === "number" ? (
            <button
              key={idx}
              type="button"
              onClick={() => onChange(p)}
              className={`${btnBase} ${p === clamped ? btnActive : btnGhost}`}
              aria-current={p === clamped ? "page" : undefined}
              aria-label={`Page ${p}`}
            >
              {p}
            </button>
          ) : (
            <span key={idx} className="px-2 text-gray-400 select-none">
              …
            </span>
          )
        )}

        <button
          type="button"
          className={iconBtn}
          onClick={() => onChange(clamped + 1)}
          disabled={clamped === totalPages}
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
        <button
          type="button"
          className={iconBtn}
          onClick={() => onChange(totalPages)}
          disabled={clamped === totalPages}
          aria-label="Last page"
        >
          <ChevronsRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
export default function AdminDashboard() {
  const [landlords, setLandlords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [resetModal, setResetModal] = useState(null);
  const [successModal, setSuccessModal] = useState(false);
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [loadingSaveEdit, setLoadingSaveEdit] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [loadingResetPw, setLoadingResetPw] = useState(false);
  const [search, setSearch] = useState("");

  const [resetStep, setResetStep] = useState("edit");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [copied, setCopied] = useState(false);
  const PAGE_SIZE = 15;
  const [page, setPage] = useState(1);
  const API_PAGE_SIZE = 100;

  /* ---------- 🔧 FIX: compute filteredLandlords BEFORE using it ---------- */
  const filteredLandlords = useMemo(
    () =>
      landlords.filter((l) => {
        const q = (search || "").toLowerCase();
        return (
          (l.email || "").toLowerCase().includes(q) ||
          String(l.id || "")
            .toLowerCase()
            .includes(q) ||
          (l.facilities || []).some((f) =>
            (f.name || "").toLowerCase().includes(q)
          )
        );
      }),
    [landlords, search]
  );

  // Derived pagination bits (now safe)
  const total = filteredLandlords.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);

  const pagedLandlords = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filteredLandlords.slice(start, start + PAGE_SIZE);
  }, [filteredLandlords, safePage]);

  const [form, setForm] = useState({
    email: "",
    password: "",
    facilities: [{ id: "", name: "", landlordSplit: 70 }],
  });

  const loadLandlords = async () => {
    setLoading(true);
    try {
      const users = await listAllLandlords(API_PAGE_SIZE);
      setLandlords(
        (users || []).map((u) => ({
          id: u.uid,
          email: u.email,
          createdAt: u.created_at,
          status: u.disabled ? "Disabled" : "Active",
          facilities: (u.facilities || []).map((f) => ({
            id: f.facility_id,
            name: f.facility_name,
            landlordSplit: (f.split?.landlord_share || 0) * 100,
          })),
        }))
      );
    } catch (err) {
      console.error("Error loading landlords", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("access_token")) {
      setLoading(false); // ✅ add this
      return;
    }
    loadLandlords();
  }, []);

  const totalFacilities = useMemo(
    () => landlords.reduce((acc, l) => acc + l.facilities.length, 0),
    [landlords]
  );

  const avgSplit = useMemo(() => {
    const count = totalFacilities || 1;
    const sum = landlords.reduce(
      (acc, l) =>
        acc +
        l.facilities.reduce((a, f) => a + (Number(f.landlordSplit) || 0), 0),
      0
    );
    return Math.round((sum / count) * 10) / 10;
  }, [landlords, totalFacilities]);

  const handleFacilityChange = (index, field, value) => {
    setForm((prev) => {
      const facilities = prev.facilities.map((f, i) =>
        i === index
          ? {
              ...f,
              [field]:
                field === "landlordSplit"
                  ? clamp(Number(value) || 0, 1, 99)
                  : value,
            }
          : f
      );
      return { ...prev, facilities };
    });
  };

  const addFacility = () =>
    setForm((prev) => ({
      ...prev,
      facilities: [...prev.facilities, { id: "", name: "", landlordSplit: 70 }],
    }));

  const removeFacility = (index) => {
    setForm((prev) => {
      if (prev.facilities.length === 1) return prev;
      const facilities = prev.facilities.filter((_, i) => i !== index);
      return { ...prev, facilities };
    });
  };

  // ---- Edit Modal Facility Handlers ----
  const addEditFacility = () => {
    setEditModal((prev) => ({
      ...prev,
      facilities: [...prev.facilities, { id: "", name: "", landlordSplit: 70 }],
    }));
  };

  const removeEditFacility = (index) => {
    setEditModal((prev) => {
      if (prev.facilities.length === 1) return prev;
      const facilities = prev.facilities.filter((_, i) => i !== index);
      return { ...prev, facilities };
    });
  };

  const editFacilityChange = (index, field, value) => {
    setEditModal((prev) => {
      const facilities = prev.facilities.map((f, i) =>
        i === index
          ? {
              ...f,
              [field]:
                field === "landlordSplit"
                  ? clamp(Number(value) || 0, 1, 99)
                  : value,
            }
          : f
      );
      return { ...prev, facilities };
    });
  };

  const handleCreateLandlord = async () => {
    try {
      setLoadingCreate(true);
      await createLandlord({
        email: form.email.trim(),
        password: form.password,
        landlord_id: form.email.trim(),
        facilities: form.facilities.map((f) => ({
          facility_id: f.id,
          facility_name: f.name,
          split: {
            landlord_share: f.landlordSplit / 100,
            platform_share: 1 - f.landlordSplit / 100,
          },
        })),
      });

      setOpen(false);
      setSuccessModal(true);
      await loadLandlords();
      setForm({
        email: "",
        password: "",
        facilities: [{ id: "", name: "", landlordSplit: 70 }],
      });
    } catch (err) {
      console.error("Error creating landlord", err);
    } finally {
      setLoadingCreate(false);
    }
  };

  const saveEditedLandlord = async () => {
    try {
      setLoadingSaveEdit(true);

      const payload = {
        uid: editModal.id,
        email: editModal.email,
        facilities: editModal.facilities.map((f) => ({
          facility_id: f.id,
          facility_name: f.name,
          split: {
            landlord_share: f.landlordSplit / 100,
            platform_share: 1 - f.landlordSplit / 100,
          },
        })),
      };

      await updateLandlord(editModal.id, payload);

      await loadLandlords();
      setEditModal(null);
    } catch (err) {
      console.error("Error updating landlord", err);
    } finally {
      setLoadingSaveEdit(false);
    }
  };

  const confirmDeleteLandlord = async () => {
    try {
      setLoadingDelete(true);
      await deleteLandlord(deleteModal);
      await loadLandlords();
      setDeleteModal(null);
    } catch (err) {
      console.error("Error deleting landlord", err);
    } finally {
      setLoadingDelete(false);
    }
  };

  const saveNewPassword = async () => {
    try {
      setLoadingResetPw(true);
      await changePassword(resetModal, newPassword);
      setResetStep("saved");
    } catch (err) {
      console.error("Error resetting password", err);
    } finally {
      setLoadingResetPw(false);
    }
  };

  const resetPassword = (landlordId) => {
    setNewPassword("");
    setConfirmPassword("");
    setShowPw(false);
    setCopied(false);
    setResetStep("edit");
    setResetModal(landlordId);
  };

  return (
    <div
      className="min-h-[100dvh] overflow-x-clip 
  bg-[radial-gradient(ellipse_at_top_left,rgba(99,102,241,0.18),transparent_55%),radial-gradient(ellipse_at_bottom_right,rgba(168,85,247,0.18),transparent_55%)]
  p-4 sm:p-6 lg:p-8 
  pt-10 sm:pt-14 lg:pt-24 
  pb-[env(safe-area-inset-bottom)]"
    >
      {loading && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white/80 backdrop-blur-sm">
          <Loader />
        </div>
      )}
      <div className="mx-auto w-full max-w-7xl flex flex-col items-center">
        {/* Header */}
        <div className=" w-full flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between text-center sm:text-left">
          <div className="flex items-center justify-center sm:justify-start gap-3">
            <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-600 text-white grid place-items-center shadow-lg">
              🏢
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold">
                Admin · Landlords
              </h1>
              <p className="text-xs sm:text-sm text-gray-500">
                Manage landlords, facilities & revenue splits
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-center sm:justify-end gap-2">
            <span className="hidden sm:inline-flex px-3 py-1 rounded-xl text-xs bg-white/60 backdrop-blur border">
              {landlords.length} landlords
            </span>
            <span className="hidden sm:inline-flex px-3 py-1 rounded-xl text-xs bg-white/60 backdrop-blur border">
              {totalFacilities} facilities
            </span>
            <button
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-600 text-white shadow hover:shadow-md active:scale-[0.99] transition text-sm sm:text-base"
            >
              <Plus className="h-4 w-4" /> Add Landlord
            </button>
            <input
              type="text"
              placeholder="Search landlords..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full sm:w-64 rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>
        </div>

        {/* KPIs */}
        <div className="w-full grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6 text-center sm:text-left">
          <div className="rounded-2xl border bg-white/70 backdrop-blur p-4 shadow-sm">
            <div className="text-xs text-gray-500">Landlords</div>
            <div className="text-2xl font-semibold mt-1">
              {landlords.length}
            </div>
          </div>
          <div className="rounded-2xl border bg-white/70 backdrop-blur p-4 shadow-sm">
            <div className="text-xs text-gray-500">Facilities</div>
            <div className="text-2xl font-semibold mt-1">{totalFacilities}</div>
          </div>
          <div className="rounded-2xl border bg-white/70 backdrop-blur p-4 shadow-sm">
            <div className="text-xs text-gray-500">Avg Landlord Split</div>
            <div className="text-2xl font-semibold mt-1">{avgSplit}%</div>
          </div>
        </div>

        {/* Table (only this scrolls horizontally) */}
        <div className="w-full mt-6 rounded-2xl border bg-white/70 backdrop-blur shadow-sm">
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full table-auto text-sm">
              <thead className="bg-gray-50/70 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-left">
                    #
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-left">
                    Landlord
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-left">
                    Facilities
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-left">
                    Created
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-left">
                    Split %
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {pagedLandlords.map((l, i) => (
                  <tr
                    key={l.id}
                    className="border-t hover:bg-blue-50/40 transition"
                  >
                    <td className="px-4 py-3 align-top">
                      {(safePage - 1) * PAGE_SIZE + i + 1}
                    </td>
                    <td className="px-4 py-3 align-top whitespace-normal break-words">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white grid place-items-center text-sm">
                          {l.email?.charAt(0)?.toUpperCase()}
                        </div>
                        <div>
                          <div className="text-sm font-medium break-words">
                            {l.email}
                          </div>
                          <div className="text-xs text-gray-500">{l.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm align-top whitespace-normal break-words">
                      {l.facilities.map((f) => (
                        <div key={f.id}>
                          {f.name} ({f.id})
                        </div>
                      ))}
                    </td>
                    <td className="px-4 py-3 text-sm align-top">
                      {prettyDate(l.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-sm align-top">
                      {Math.round(
                        (l.facilities.reduce(
                          (a, f) => a + f.landlordSplit,
                          0
                        ) || 0) / (l.facilities.length || 1)
                      )}
                      %
                    </td>
                    <td className="px-4 py-3 text-right align-top flex items-center justify-end gap-3">
                      <button
                        onClick={() => setEditModal(l)}
                        className="inline-flex items-center gap-1 text-xs text-green-600 hover:underline"
                      >
                        <Edit3 className="h-3 w-3" /> Edit
                      </button>
                      <button
                        onClick={() => resetPassword(l.id)}
                        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                      >
                        <Key className="h-3 w-3" /> Reset
                      </button>
                      <button
                        onClick={() => setDeleteModal(l.id)}
                        className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline"
                      >
                        <Trash2 className="h-3 w-3" /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {pagedLandlords.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-6 text-center text-sm text-gray-500"
                    >
                      No landlords found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="block sm:hidden divide-y">
            {pagedLandlords.map((l, i) => (
              <div key={l.id} className="p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white grid place-items-center text-sm">
                    {l.email?.charAt(0)?.toUpperCase()}
                  </div>
                  <div>
                    <div className="text-sm font-medium break-all">
                      {l.email}
                    </div>
                    <div className="text-xs text-gray-500">{l.id}</div>
                  </div>
                </div>
                <div className="text-xs text-gray-600">
                  <span className="font-medium">#</span>{" "}
                  {(safePage - 1) * PAGE_SIZE + i + 1}
                </div>
                <div className="text-xs text-gray-600">
                  <span className="font-medium">Facilities:</span>{" "}
                  {l.facilities.map((f) => `${f.name} (${f.id})`).join(", ")}
                </div>
                <div className="text-xs text-gray-600">
                  <span className="font-medium">Created:</span>{" "}
                  {prettyDate(l.createdAt)}
                </div>
                <div className="text-xs text-gray-600">
                  <span className="font-medium">Split %:</span>{" "}
                  {Math.round(
                    (l.facilities.reduce((a, f) => a + f.landlordSplit, 0) ||
                      0) / (l.facilities.length || 1)
                  )}
                  %
                </div>
                <div className="mt-2 flex items-center justify-end gap-3">
                  <button
                    onClick={() => setEditModal(l)}
                    className="inline-flex items-center gap-1 text-xs text-green-600 hover:underline"
                  >
                    <Edit3 className="h-3 w-3" /> Edit
                  </button>
                  <button
                    onClick={() => resetPassword(l.id)}
                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                  >
                    <Key className="h-3 w-3" /> Reset Password
                  </button>
                  <button
                    onClick={() => setDeleteModal(l.id)}
                    className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline"
                  >
                    <Trash2 className="h-3 w-3" /> Delete
                  </button>
                </div>
              </div>
            ))}
            {pagedLandlords.length === 0 && (
              <div className="p-6 text-center text-sm text-gray-500">
                No landlords found.
              </div>
            )}
          </div>

          {/* Pagination footer */}
          <Pagination
            total={total}
            page={safePage}
            pageSize={PAGE_SIZE}
            onChange={(p) => setPage(p)}
          />
        </div>

        {/* Success Modal */}
        {successModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
            {/* Overlay */}
            <div
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={() => setSuccessModal(false)}
            />

            {/* Panel */}
            <div
              className="
        relative z-10
        w-full sm:max-w-md
        max-h-[90vh]
        bg-white border shadow-2xl
        rounded-xl sm:rounded-2xl
        flex flex-col
        p-6
        text-center
        animate-[fadeInUp_0.25s_ease-out]
        overflow-y-auto
      "
              role="dialog"
              aria-modal="true"
              aria-label="Landlord Created"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-lg sm:text-xl font-semibold text-green-600">
                🎉 Landlord Created Successfully!
              </h2>
              <p className="text-sm text-gray-600 mt-2">
                {form.email} has been registered.
              </p>

              <button
                onClick={() => setSuccessModal(false)}
                className="mt-6 px-5 py-2 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow hover:shadow-md"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {editModal && (
          <div className="fixed inset-0 z-50 flex justify-center sm:items-center p-2 sm:p-4">
            <div
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={() => setEditModal(null)}
            />
            <div
              className="relative z-10 w-full sm:max-w-2xl bg-white rounded-2xl shadow-2xl border flex flex-col max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex justify-between items-center px-6 py-4 border-b bg-white/90 sticky top-0 z-10">
                <h2 className="text-lg font-semibold">
                  Edit Landlord ·{" "}
                  <span className="text-gray-600">{editModal.email}</span>
                </h2>
                <button
                  onClick={() => setEditModal(null)}
                  className="p-2 hover:bg-gray-100 rounded-xl"
                >
                  <X className="h-5 w-5 text-gray-600" />
                </button>
              </div>

              {/* Scrollable Body */}
              <Formik
                initialValues={{
                  facilities: editModal.facilities || [],
                }}
                validationSchema={Yup.object({
                  facilities: Yup.array().of(
                    Yup.object({
                      id: Yup.string()
                        .matches(
                          /^[0-9]+$/,
                          "Facility ID must contain numbers only"
                        )
                        .required("Facility ID required"),
                      name: Yup.string().required("Facility name required"),
                      landlordSplit: Yup.number().min(1).max(99).required(),
                    })
                  ),
                })}
                onSubmit={async (values, { setSubmitting }) => {
                  try {
                    setLoadingSaveEdit(true);
                    const payload = {
                      uid: editModal.id,
                      email: editModal.email,
                      facilities: values.facilities.map((f) => ({
                        facility_id: f.id,
                        facility_name: f.name,
                        split: {
                          landlord_share: f.landlordSplit / 100,
                          platform_share: 1 - f.landlordSplit / 100,
                        },
                      })),
                    };
                    await updateLandlord(editModal.id, payload);
                    await loadLandlords();
                    setEditModal(null);
                  } catch (err) {
                    console.error("Error saving landlord", err);
                  } finally {
                    setLoadingSaveEdit(false);
                    setSubmitting(false);
                  }
                }}
              >
                {({ values, handleChange }) => (
                  <Form className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
                    <FieldArray
                      name="facilities"
                      render={(arrayHelpers) => (
                        <>
                          <div className="flex justify-between items-center">
                            <h3 className="text-sm font-medium">Facilities</h3>
                            <button
                              type="button"
                              className="text-sm text-blue-600 hover:underline"
                              onClick={() =>
                                arrayHelpers.push({
                                  id: "",
                                  name: "",
                                  landlordSplit: 70,
                                })
                              }
                            >
                              + Add Facility
                            </button>
                          </div>

                          {values.facilities.map((f, i) => (
                            <div
                              key={i}
                              className="rounded-xl border bg-gray-50 p-4 relative"
                            >
                              {values.facilities.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => arrayHelpers.remove(i)}
                                  className="absolute top-3 right-3 text-gray-400 hover:text-red-600"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                              <div className="grid gap-4 sm:grid-cols-2">
                                <div>
                                  <label className="text-sm font-medium">
                                    Facility ID
                                  </label>
                                  <Field
                                    name={`facilities[${i}].id`}
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    onInput={(e) => {
                                      // Strip out non-numeric characters
                                      e.target.value = e.target.value.replace(
                                        /[^0-9]/g,
                                        ""
                                      );
                                    }}
                                    className="mt-1 w-full rounded-lg border px-3 py-2 text-sm outline-none"
                                  />
                                  <ErrorMessage
                                    name={`facilities[${i}].id`}
                                    component="p"
                                    className="text-xs text-red-600 mt-1"
                                  />
                                </div>

                                <div>
                                  <label className="text-sm font-medium">
                                    Facility Name
                                  </label>
                                  <Field
                                    name={`facilities[${i}].name`}
                                    className="mt-1 w-full rounded-lg border px-3 py-2 text-sm outline-none"
                                  />
                                  <ErrorMessage
                                    name={`facilities[${i}].name`}
                                    component="p"
                                    className="text-xs text-red-600 mt-1"
                                  />
                                </div>
                              </div>

                              <div className="mt-3 sm:mt-4 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center">
                                <div>
                                  <label className="text-sm font-medium flex items-center gap-2">
                                    <Percent className="h-4 w-4" /> Landlord
                                    Split (%)
                                  </label>
                                  <div className="mt-2 flex items-center gap-3">
                                    <Field
                                      type="range"
                                      name={`facilities[${i}].landlordSplit`}
                                      min="1"
                                      max="99"
                                      className="w-full accent-blue-600"
                                    />
                                    <Field
                                      type="number"
                                      name={`facilities[${i}].landlordSplit`}
                                      min="1"
                                      max="99"
                                      className="w-20 rounded-lg border px-2 py-1 text-right text-sm"
                                    />
                                  </div>
                                  <ErrorMessage
                                    name={`facilities[${i}].landlordSplit`}
                                    component="p"
                                    className="text-xs text-red-600 mt-1"
                                  />
                                </div>

                                {/* Split badge on the right */}
                                <div className="justify-self-start sm:justify-self-end text-xs mt-1 sm:mt-0">
                                  <div className="inline-flex items-center gap-2 rounded-full border px-2.5 py-1 bg-white text-xs sm:text-sm">
                                    <span className="font-medium">
                                      {values.facilities[i].landlordSplit}%
                                    </span>
                                    <span className="text-gray-500">
                                      Landlord
                                    </span>
                                    <span className="text-gray-300">•</span>
                                    <span className="font-medium">
                                      {100 -
                                        (Number(
                                          values.facilities[i].landlordSplit
                                        ) || 0)}
                                      %
                                    </span>
                                    <span className="text-gray-500">
                                      Platform
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                    />

                    {/* Footer */}
                    <div className="flex justify-end gap-3 border-t pt-3">
                      <button
                        type="button"
                        onClick={() => setEditModal(null)}
                        className="px-4 py-2 rounded-xl border hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={loadingSaveEdit}
                        className="px-5 py-2 rounded-xl bg-green-600 text-white shadow"
                      >
                        {loadingSaveEdit ? "Saving..." : "Save Changes"}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteModal && (
          <div className="fixed inset-0 z-50">
            <div
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={() => setDeleteModal(null)}
            />
            <div className="absolute inset-0 grid place-items-center p-4">
              <div className="w-[92vw] sm:max-w-md bg-white rounded-2xl shadow-2xl border overflow-hidden flex flex-col">
                <div className="px-6 py-4 border-b">
                  <h2 className="text-lg font-semibold text-center sm:text-left">
                    Confirm Delete
                  </h2>
                </div>
                <div className="px-6 py-5 text-sm text-gray-600">
                  Are you sure you want to delete this landlord? This action
                  cannot be undone.
                </div>
                <div className="px-6 py-4 border-t flex flex-col sm:flex-row justify-end gap-3">
                  <button
                    onClick={() => setDeleteModal(null)}
                    className="w-full sm:w-auto px-4 py-2 rounded-xl border hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDeleteLandlord}
                    disabled={loadingDelete}
                    className="w-full sm:w-auto px-5 py-2 rounded-xl bg-red-600 text-white shadow hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingDelete ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Landlord Modal */}
        {open && (
          <div className="fixed inset-0 z-50">
            <div
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={() => setOpen(false)}
            />
            <div className="absolute inset-0 grid place-items-center p-3 sm:p-4">
              <div className="w-[92vw] sm:w-full sm:max-w-2xl max-h-[88vh] bg-white rounded-2xl shadow-2xl border overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b bg-white/80 backdrop-blur">
                  <div className="text-center sm:text-left w-full">
                    <h2 className="text-lg font-semibold">Add New Landlord</h2>
                    <p className="text-xs text-gray-500">
                      Invite landlord and assign facilities with revenue split
                    </p>
                  </div>
                  <button
                    aria-label="Close"
                    className="hidden sm:inline-flex rounded-xl p-2 hover:bg-gray-100"
                    onClick={() => setOpen(false)}
                  >
                    <X className="h-5 w-5 text-gray-600" />
                  </button>
                </div>

                {/* Formik Form */}
                <Formik
                  initialValues={{
                    email: "",
                    password: "",
                    facilities: [{ id: "", name: "", landlordSplit: 70 }],
                  }}
                  validationSchema={Yup.object({
                    email: Yup.string()
                      .email("Invalid email")
                      .required("Email is required"),
                    password: Yup.string()
                      .min(8, "Minimum 8 characters")
                      .required("Password is required"),
                    facilities: Yup.array().of(
                      Yup.object({
                        id: Yup.string()
                          .matches(
                            /^[0-9]+$/,
                            "Facility ID must be numbers only"
                          )
                          .required("Facility ID required"),
                        name: Yup.string().required("Facility name required"),
                        landlordSplit: Yup.number()
                          .min(1, "Min 1%")
                          .max(99, "Max 99%")
                          .required("Split required"),
                      })
                    ),
                  })}
                  onSubmit={async (values, { setSubmitting, resetForm }) => {
                    try {
                      setLoadingCreate(true);
                      await createLandlord({
                        email: values.email.trim(),
                        password: values.password,
                        landlord_id: values.email.trim(),
                        facilities: values.facilities.map((f) => ({
                          facility_id: f.id,
                          facility_name: f.name,
                          split: {
                            landlord_share: f.landlordSplit / 100,
                            platform_share: 1 - f.landlordSplit / 100,
                          },
                        })),
                      });
                      resetForm();
                      setOpen(false);
                      setSuccessModal(true);
                      await loadLandlords();
                    } catch (err) {
                      console.error("Error creating landlord", err);
                    } finally {
                      setLoadingCreate(false);
                      setSubmitting(false);
                    }
                  }}
                >
                  {({ values }) => (
                    <Form className="flex flex-col flex-1 min-h-0 overflow-y-auto">
                      <div className="px-4 sm:px-6 py-5 space-y-6 flex-1 overflow-y-auto">
                        {/* Email + Password */}
                        <div className="grid gap-4 sm:grid-cols-2">
                          <div>
                            <label className="block text-sm font-medium mb-1">
                              Email
                            </label>
                            <div className="relative">
                              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                              <Field
                                type="email"
                                name="email"
                                placeholder="name@company.com"
                                className="w-full rounded-xl border px-9 py-2 outline-none focus:ring-2 focus:ring-blue-200"
                              />
                            </div>
                            <ErrorMessage
                              name="email"
                              component="p"
                              className="text-xs text-red-600 mt-1"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">
                              Password
                            </label>
                            <div className="relative">
                              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                              <Field
                                type="password"
                                name="password"
                                placeholder="••••••••"
                                className="w-full rounded-xl border px-9 py-2 outline-none focus:ring-2 focus:ring-blue-200"
                              />
                            </div>
                            <ErrorMessage
                              name="password"
                              component="p"
                              className="text-xs text-red-600 mt-1"
                            />
                          </div>
                        </div>

                        {/* Facilities */}
                        <FieldArray
                          name="facilities"
                          render={(arrayHelpers) => (
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <h3 className="text-sm font-medium">
                                  Facilities
                                </h3>
                                <button
                                  type="button"
                                  onClick={() =>
                                    arrayHelpers.push({
                                      id: "",
                                      name: "",
                                      landlordSplit: 70,
                                    })
                                  }
                                  className="text-sm text-blue-600 hover:underline"
                                >
                                  + Add Facility
                                </button>
                              </div>

                              {values.facilities.map((f, i) => {
                                const platformSplit =
                                  100 - (Number(f.landlordSplit) || 0);
                                return (
                                  <div
                                    key={i}
                                    className="relative rounded-xl border bg-gray-50 p-4"
                                  >
                                    {values.facilities.length > 1 && (
                                      <button
                                        type="button"
                                        onClick={() => arrayHelpers.remove(i)}
                                        className="absolute right-3 top-3 text-gray-400 hover:text-red-600"
                                        aria-label="Remove facility"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    )}

                                    <div className="grid gap-4 sm:grid-cols-2">
                                      <div>
                                        <label className="text-sm font-medium">
                                          Facility ID
                                        </label>
                                        <Field
                                          name={`facilities[${i}].id`}
                                          type="text"
                                          inputMode="numeric"
                                          pattern="[0-9]*"
                                          onInput={(e) => {
                                            // strip out non-numeric characters
                                            e.target.value =
                                              e.target.value.replace(
                                                /[^0-9]/g,
                                                ""
                                              );
                                          }}
                                          className="mt-1 w-full rounded-lg border px-3 py-2 outline-none focus:ring-2 focus:ring-blue-200"
                                        />
                                        <ErrorMessage
                                          name={`facilities[${i}].id`}
                                          component="p"
                                          className="text-xs text-red-600 mt-1"
                                        />
                                      </div>
                                      <div>
                                        <label className="text-sm font-medium">
                                          Facility Name
                                        </label>
                                        <Field
                                          name={`facilities[${i}].name`}
                                          className="mt-1 w-full rounded-lg border px-3 py-2 outline-none focus:ring-2 focus:ring-blue-200"
                                        />
                                        <ErrorMessage
                                          name={`facilities[${i}].name`}
                                          component="p"
                                          className="text-xs text-red-600 mt-1"
                                        />
                                      </div>
                                    </div>

                                    <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center">
                                      <div>
                                        <label className="text-sm font-medium flex items-center gap-2">
                                          <Percent className="h-4 w-4" />{" "}
                                          Landlord Split (%)
                                        </label>
                                        <div className="mt-2 flex items-center gap-3">
                                          <Field
                                            type="range"
                                            name={`facilities[${i}].landlordSplit`}
                                            min="1"
                                            max="99"
                                            className="w-full accent-blue-600"
                                          />
                                          <Field
                                            type="number"
                                            name={`facilities[${i}].landlordSplit`}
                                            min="1"
                                            max="99"
                                            className="w-20 rounded-lg border px-2 py-1 text-right"
                                          />
                                        </div>
                                        <ErrorMessage
                                          name={`facilities[${i}].landlordSplit`}
                                          component="p"
                                          className="text-xs text-red-600 mt-1"
                                        />
                                      </div>
                                      <div className="justify-self-end text-xs">
                                        <div className="inline-flex items-center gap-2 rounded-full border px-3 py-1 bg-white">
                                          <span className="font-medium">
                                            {f.landlordSplit}%
                                          </span>
                                          <span className="text-gray-500">
                                            Landlord
                                          </span>
                                          <span className="text-gray-300">
                                            •
                                          </span>
                                          <span className="font-medium">
                                            {platformSplit}%
                                          </span>
                                          <span className="text-gray-500">
                                            Platform
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        />
                      </div>

                      {/* Footer inside the form */}
                      <div className="px-4 sm:px-6 py-4 border-t bg-white/80 backdrop-blur flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => setOpen(false)}
                          className="w-full sm:w-auto px-4 py-2 rounded-xl border hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={loadingCreate}
                          className="w-full sm:w-auto px-5 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-600 text-white shadow hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loadingCreate ? "Creating..." : "Create Landlord"}
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        )}

        {/* Reset Password Modal */}
        {resetModal && (
          <div className="fixed inset-0 z-50">
            <div
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={() => setResetModal(null)}
            />
            <div className="absolute inset-0 grid place-items-center p-3 sm:p-4">
              <div className="w-[92vw] sm:w-full sm:max-w-md bg-white rounded-2xl shadow-2xl border overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b bg-white/80 backdrop-blur">
                  <h2 className="text-lg font-semibold w-full text-center sm:text-left">
                    {resetStep === "edit" ? "Reset Password" : "Copy Password"}
                  </h2>
                  <button
                    onClick={() => setResetModal(null)}
                    className="hidden sm:inline-flex p-2 hover:bg-gray-100 rounded-xl"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {/* Body */}
                {resetStep === "edit" ? (
                  <Formik
                    initialValues={{
                      newPassword: "",
                      confirmPassword: "",
                    }}
                    validationSchema={Yup.object({
                      newPassword: Yup.string()
                        .min(8, "Password must be at least 8 characters")
                        .required("New password required"),
                      confirmPassword: Yup.string()
                        .oneOf([Yup.ref("newPassword")], "Passwords must match")
                        .required("Confirm password required"),
                    })}
                    onSubmit={async (values, { setSubmitting }) => {
                      try {
                        setLoadingResetPw(true);
                        await changePassword(resetModal, values.newPassword);
                        setNewPassword(values.newPassword); // store it for "Copy Password" step
                        setResetStep("saved");
                      } catch (err) {
                        console.error("Error resetting password", err);
                      } finally {
                        setLoadingResetPw(false);
                        setSubmitting(false);
                      }
                    }}
                  >
                    {() => (
                      <Form className="p-4 sm:p-6 space-y-4">
                        <p className="text-sm text-gray-600 text-center sm:text-left">
                          Enter a new password and confirm. Minimum 8
                          characters.
                        </p>

                        {/* New Password */}
                        <div>
                          <label className="text-sm font-medium">
                            New Password
                          </label>
                          <div className="relative mt-1">
                            <Field
                              type={showPw ? "text" : "password"}
                              name="newPassword"
                              className="w-full rounded-xl border px-3 py-2 pr-10 outline-none focus:ring-2 focus:ring-blue-200"
                            />
                            <button
                              type="button"
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800"
                              onClick={() => setShowPw((s) => !s)}
                              aria-label={
                                showPw ? "Hide password" : "Show password"
                              }
                            >
                              {showPw ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                          <ErrorMessage
                            name="newPassword"
                            component="p"
                            className="text-xs text-red-600 mt-1"
                          />
                        </div>

                        {/* Confirm Password */}
                        <div>
                          <label className="text-sm font-medium">
                            Confirm Password
                          </label>
                          <Field
                            type="password"
                            name="confirmPassword"
                            className="w-full rounded-xl border px-3 py-2 outline-none focus:ring-2 focus:ring-blue-200 mt-1"
                          />
                          <ErrorMessage
                            name="confirmPassword"
                            component="p"
                            className="text-xs text-red-600 mt-1"
                          />
                        </div>

                        {/* Footer */}
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 pt-4 border-t">
                          <button
                            type="button"
                            onClick={() => setResetModal(null)}
                            className="w-full sm:w-auto px-4 py-2 rounded-xl border hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={loadingResetPw}
                            className="w-full sm:w-auto px-5 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-600 text-white shadow disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {loadingResetPw ? "Saving..." : "Save"}
                          </button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                ) : (
                  <div className="p-4 sm:p-6 space-y-4">
                    <p className="text-sm text-gray-600 text-center sm:text-left">
                      Copy and share this password with the landlord. You won't
                      be able to see it again.
                    </p>
                    <div className="flex items-center gap-2 bg-gray-50 border rounded-xl px-3 py-2">
                      <span className="font-mono text-sm flex-1 select-all">
                        {newPassword}
                      </span>
                      <button
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(newPassword);
                            setCopied(true);
                            setTimeout(() => setCopied(false), 1500);
                          } catch {}
                        }}
                        className="text-blue-600 flex items-center gap-1 text-xs"
                      >
                        <Copy className="h-4 w-4" />{" "}
                        {copied ? "Copied" : "Copy"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
